/**
 * Truck Schedule Component
 * Fetches Google Calendar events and displays them in a weekly grid
 * Handles timezone conversion for US timezones (ET, CT, PT)
 * Supports cross-brand secondary calendar toggle (e.g., CML on CFS pages)
 */

class TruckScheduleComponent extends HTMLElement {
  constructor() {
    super();
    this.events = [];
    this.days = [];
    this.secondaryEvents = [];
    this.secondaryDays = [];
  }

  connectedCallback() {
    this.apiUrl = this.dataset.apiUrl;
    this.orderUrl = this.dataset.orderUrl || '';
    this.orderButtonText = this.dataset.orderButtonText || 'Order Now';
    this.closedText = this.dataset.closedText || 'Closed';
    this.filterState = (this.dataset.filterState || '').toUpperCase();
    this.hidePrivateEvents = this.dataset.hidePrivateEvents === 'true';
    this.disableMobileAccordion = this.dataset.disableMobileAccordion === 'true';

    // Secondary calendar (cross-brand) config
    this.secondaryApiUrl = this.dataset.secondaryApiUrl || '';
    this.secondaryBrandName = this.dataset.secondaryBrandName || '';
    this.secondaryOrderUrl = this.dataset.secondaryOrderUrl || '';
    this.secondaryOrderButtonText = this.dataset.secondaryOrderButtonText || 'Order Now';
    this.hideSecondaryOrder = this.dataset.hideSecondaryOrder === 'true';
    // The other brand's events are always filtered to THIS location's state,
    // so the banner only appears where that brand is actually "serving nearby".
    this.crossBrandState = (this.dataset.crossBrandState || '').toUpperCase();

    if (!this.apiUrl) {
      console.warn('Truck Schedule: No API URL provided');
      this.renderError('Calendar not configured');
      return;
    }

    this.initDays();
    this.fetchEvents();

    if (this.secondaryApiUrl) {
      this.initCrossBrand();
    }
  }

  /**
   * Both brand sites anchor their 7-day window on today's date in Los Angeles
   * (the CML site's DetailSchedule.js does this for every location), not on
   * the visitor's local date. Using one fixed US reference keeps the CFS and
   * CML schedules starting on the same day everywhere, including on the other
   * brand's site, no matter where the visitor is.
   */
  static SCHEDULE_TIMEZONE = 'America/Los_Angeles';

  /**
   * Build a 7-day array (today + next 6 days) in the schedule timezone.
   */
  buildDays() {
    const today = this.toCalendarDate(new Date(), TruckScheduleComponent.SCHEDULE_TIMEZONE);
    const days = [];

    for (let i = 0; i < 7; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);

      days.push({
        date: date,
        dayAbbr: date.toLocaleDateString('en-US', { weekday: 'short' }).toUpperCase(),
        dayDate: this.formatDateWithOrdinal(date),
        events: [],
      });
    }

    return days;
  }

  /**
   * Initialize the 7-day array for the primary calendar
   */
  initDays() {
    this.days = this.buildDays();
  }

  /**
   * Initialize a separate 7-day array for the secondary (cross-brand) calendar
   */
  initSecondaryDays() {
    this.secondaryDays = this.buildDays();
  }

  /**
   * Format date with ordinal suffix (e.g., "MAR 20TH")
   */
  formatDateWithOrdinal(date) {
    const month = date.toLocaleDateString('en-US', { month: 'short' }).toUpperCase();
    const day = date.getDate();
    const suffix = this.getOrdinalSuffix(day);
    return `${month} ${day}${suffix}`;
  }

  /*
   * a11y #119 — why the day headings carry a trailing `&nbsp;`.
   *
   * The weekday and the date are separate spans inside a flex container, and
   * the visible space between them is drawn by `gap` (4px in the table header,
   * 6px in the accordion). `gap` is layout, not text, so a screen reader reads
   * the two fragments with nothing between them: "WEDJUL 4TH".
   *
   * The remedy is a real space character in the text, per the Accessible360
   * guidance. It has to be `&nbsp;` rather than a plain space: these spans are
   * flex items, and a plain trailing space sits at the end of the item's line
   * box, where CSS white-space processing trims it away — so it would be
   * dropped again. `&nbsp;` does not collapse and survives into the
   * accessibility tree.
   */

  /**
   * Get ordinal suffix for a number (ST, ND, RD, TH)
   */
  getOrdinalSuffix(n) {
    if (n >= 11 && n <= 13) return 'TH';
    switch (n % 10) {
      case 1: return 'ST';
      case 2: return 'ND';
      case 3: return 'RD';
      default: return 'TH';
    }
  }

  /**
   * Fetch events from the calendar API
   */
  async fetchEvents() {
    try {
      this.renderLoading();

      const response = await fetch(this.apiUrl);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      if (data.error) {
        throw new Error(data.error);
      }

      this.events = data.events || [];
      this.filterEventsByState();
      this.filterPrivateEvents();
      this.assignEventsToDay();
      this.render();
    } catch (error) {
      console.error('Truck Schedule: Failed to fetch events', error);
      this.renderError('Unable to load schedule');
    }
  }

  /**
   * Filter events by state code if a filter is set.
   * Matches state code against event location or title using word boundary check
   * to avoid partial matches (e.g., "IN" matching "MAIN").
   */
  filterEventsByState() {
    if (!this.filterState) return;

    const state = this.filterState;
    // Regex to match state code as a standalone word (e.g., ", KY" or "KY 40202")
    const stateRegex = new RegExp(`\\b${state}\\b`, 'i');

    this.events = this.events.filter(event => {
      const location = event.location || '';
      const title = event.title || '';
      return stateRegex.test(location) || stateRegex.test(title);
    });
  }

  /**
   * Hide "Private Event" stops from the main calendar when the section setting
   * is unchecked. Matched as a substring of the event title so variants like
   * "PRIVATE EVENT - Smith Wedding" are caught too.
   */
  filterPrivateEvents() {
    if (!this.hidePrivateEvents) return;

    this.events = this.events.filter(event => {
      const title = (event.title || '').toLowerCase();
      return !title.includes('private event');
    });
  }

  /**
   * Check if a date string is a date-only format (all-day event)
   * Google Calendar returns "2026-05-07" for all-day events vs "2026-05-07T12:00:00-04:00" for timed
   */
  isDateOnly(dateStr) {
    return typeof dateStr === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateStr);
  }

  /**
   * Parse a date string as local date (not UTC).
   * Date-only strings like "2026-05-07" are parsed as local midnight
   * to avoid timezone shift issues.
   */
  parseLocalDate(dateStr) {
    if (this.isDateOnly(dateStr)) {
      const [year, month, day] = dateStr.split('-').map(Number);
      return new Date(year, month - 1, day);
    }
    return new Date(dateStr);
  }

  /**
   * Get the calendar date of an instant as seen in the given timezone,
   * returned as local midnight so it compares directly against `day.date`.
   */
  toCalendarDate(date, timezone) {
    try {
      const parts = new Intl.DateTimeFormat('en-US', {
        timeZone: timezone || 'America/New_York',
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
      }).formatToParts(date);
      const get = type => Number(parts.find(part => part.type === type).value);
      return new Date(get('year'), get('month') - 1, get('day'));
    } catch {
      const fallback = new Date(date);
      fallback.setHours(0, 0, 0, 0);
      return fallback;
    }
  }

  /**
   * Get the first and last calendar day an event covers.
   * Timed events are resolved in the event's own timezone (as the brand sites
   * do), not the viewer's — otherwise an 11AM-7PM ET stop spans two days for
   * anyone viewing from far outside the US.
   */
  getEventDayBounds(event, isAllDay) {
    if (isAllDay) {
      // All-day end dates from Google Calendar are exclusive
      // (e.g. start=May 7, end=May 10 means May 7-9), so subtract one day.
      const startDate = this.parseLocalDate(event.start);
      const lastDate = this.parseLocalDate(event.end);
      lastDate.setDate(lastDate.getDate() - 1);
      return { startDate, lastDate };
    }

    const start = new Date(event.start);
    // Step back 1ms so a stop ending exactly at midnight stays on its own day.
    const end = new Date(new Date(event.end).getTime() - 1);
    return {
      startDate: this.toCalendarDate(start, event.timezone),
      lastDate: this.toCalendarDate(end < start ? start : end, event.timezone),
    };
  }

  /**
   * Assign events to their respective days.
   * Multi-day events are expanded so they appear on each day they span.
   * Days are resolved in the event's own timezone (see getEventDayBounds), so
   * an 11AM-7PM ET stop lands on one day for every visitor.
   */
  assignEventsToDay() {
    // Reset events
    this.days.forEach(day => day.events = []);

    this.events.forEach(event => {
      const isAllDay = event.isAllDay || this.isDateOnly(event.start);
      const { startDate, lastDate } = this.getEventDayBounds(event, isAllDay);

      const eventData = {
        ...event,
        isAllDay,
        formattedTime: isAllDay ? this.closedText : this.formatTimeRange(event.start, event.end, event.timezone),
        parsedLocation: this.parseLocation(event.location, event.title, event.description),
      };

      // Place event on every day it spans within the visible range
      this.days.forEach(day => {
        if (day.date.getTime() >= startDate.getTime() && day.date.getTime() <= lastDate.getTime()) {
          day.events.push(eventData);
        }
      });
    });
  }

  /**
   * Assign secondary events to their respective days (same logic, separate data).
   */
  assignSecondaryEventsToDay() {
    this.secondaryDays.forEach(day => day.events = []);

    this.secondaryEvents.forEach(event => {
      const isAllDay = event.isAllDay || this.isDateOnly(event.start);
      const { startDate, lastDate } = this.getEventDayBounds(event, isAllDay);

      const eventData = {
        ...event,
        isAllDay,
        formattedTime: isAllDay ? this.closedText : this.formatSecondaryTimeRange(event.start, event.end, event.timezone),
        parsedLocation: this.parseLocation(event.location, event.title, event.description),
      };

      this.secondaryDays.forEach(day => {
        if (day.date.getTime() >= startDate.getTime() && day.date.getTime() <= lastDate.getTime()) {
          day.events.push(eventData);
        }
      });
    });

    // Order each day like the CML site (compareEventsByTime): by start time of
    // day in the event's timezone, then by end time of day.
    this.secondaryDays.forEach(day => {
      day.events.sort((a, b) =>
        this.getMinutesOfDay(a.start, a.timezone) - this.getMinutesOfDay(b.start, b.timezone) ||
        this.getMinutesOfDay(a.end, a.timezone) - this.getMinutesOfDay(b.end, b.timezone)
      );
    });
  }

  /**
   * Minutes past midnight of a start/end value in the event's timezone.
   * Date-only (all-day) values count as midnight, as they do on the CML site.
   */
  getMinutesOfDay(dateStr, timezone) {
    if (!dateStr || this.isDateOnly(dateStr)) return 0;
    try {
      const parts = new Intl.DateTimeFormat('en-US', {
        timeZone: timezone || 'America/New_York',
        hour: 'numeric',
        minute: 'numeric',
        hourCycle: 'h23',
      }).formatToParts(new Date(dateStr));
      const get = type => Number(parts.find(part => part.type === type).value);
      return get('hour') * 60 + get('minute');
    } catch {
      return 0;
    }
  }

  /**
   * Format time range with timezone indicator
   * Shows time in event's timezone with abbreviation
   */
  formatTimeRange(startStr, endStr, timezone = 'America/New_York') {
    const start = new Date(startStr);
    const end = new Date(endStr);

    const timeOptions = {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZone: timezone,
    };

    const startTime = start.toLocaleTimeString('en-US', timeOptions).toUpperCase().replace(' ', '');
    const endTime = end.toLocaleTimeString('en-US', timeOptions).toUpperCase().replace(' ', '');

    // Get timezone abbreviation
    const tzAbbr = this.getTimezoneAbbr(timezone);

    return `${startTime} - ${endTime} ${tzAbbr}`;
  }

  /**
   * Secondary (cross-brand) calendar only: same as formatTimeRange, but zones
   * outside the abbreviation map (e.g. America/Detroit, America/Toronto) come
   * back as "EDT"/"CST", so collapse them to the generic "ET"/"CT" form.
   */
  formatSecondaryTimeRange(startStr, endStr, timezone) {
    return this.formatTimeRange(startStr, endStr, timezone).replace(/\b([ECMP])[SD]T$/, '$1T');
  }

  /**
   * Get timezone abbreviation for display
   */
  getTimezoneAbbr(timezone) {
    const abbrs = {
      'America/New_York': 'ET',
      'America/Chicago': 'CT',
      'America/Denver': 'MT',
      'America/Los_Angeles': 'PT',
      'America/Phoenix': 'AZ',
    };

    if (abbrs[timezone]) {
      return abbrs[timezone];
    }

    // Fallback: try to extract from formatted string
    try {
      const date = new Date();
      const str = date.toLocaleTimeString('en-US', {
        timeZone: timezone,
        timeZoneName: 'short',
      });
      const match = str.match(/[A-Z]{2,4}$/);
      return match ? match[0] : '';
    } catch {
      return '';
    }
  }

  /**
   * Parse location and title to extract structured data
   * Expected format in title or location: "LOCATION_NAME" or "City, State"
   * Also extracts map URL from description or location field
   */
  parseLocation(location, title, description = '') {
    // Try to extract address parts
    let locationName = title || 'Location';
    let address = location || '';
    let mapUrl = '';

    // If location contains city/state format, use it as address
    // and use title as location name
    if (location && location.includes(',')) {
      address = location;
    }

    // Extract map URL from description or location
    mapUrl = this.extractMapUrl(description) || this.extractMapUrl(location);

    // Only generate Google Maps link if we have a valid address (contains comma for city/state)
    if (!mapUrl && address && address.includes(',')) {
      mapUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
    }

    return {
      name: locationName.toUpperCase(),
      address: address,
      mapUrl: mapUrl,
    };
  }

  /**
   * Extract map URL from text (Google Maps, Apple Maps, etc.)
   */
  extractMapUrl(text) {
    if (!text) return '';

    // Match various map URL patterns
    const patterns = [
      // Google Maps URLs
      /https?:\/\/(www\.)?(google\.com\/maps|maps\.google\.com|goo\.gl\/maps)[^\s<>"']*/i,
      // Apple Maps URLs
      /https?:\/\/(maps\.apple\.com)[^\s<>"']*/i,
      // Generic map links
      /https?:\/\/[^\s<>"]*maps[^\s<>"']*/i,
    ];

    for (const pattern of patterns) {
      const match = text.match(pattern);
      if (match) {
        return match[0];
      }
    }

    return '';
  }

  /**
   * Render address with optional map link
   */
  renderAddress(parsedLocation) {
    if (!parsedLocation.address) return '';

    if (parsedLocation.address && parsedLocation.mapUrl) {
      return `<a href="${parsedLocation.mapUrl}" class="c-truck-schedule__address" target="_blank" rel="noopener noreferrer">${parsedLocation.address}</a>`;
    }
    return `<span class="c-truck-schedule__address">${parsedLocation.address}</span>`;
  }

  /**
   * Render loading state
   */
  renderLoading() {
    const grid = this.querySelector('[ref="scheduleGrid"]');
    const mobile = this.querySelector('[ref="scheduleMobile"]');

    if (grid) grid.innerHTML = '<div class="c-truck-schedule__loading">Loading schedule...</div>';
    if (mobile) mobile.innerHTML = '<div class="c-truck-schedule__loading">Loading schedule...</div>';
  }

  /**
   * Render error state
   */
  renderError(message) {
    const grid = this.querySelector('[ref="scheduleGrid"]');
    const mobile = this.querySelector('[ref="scheduleMobile"]');

    const errorHtml = `<div class="c-truck-schedule__error">${message}</div>`;
    if (grid) grid.innerHTML = errorHtml;
    if (mobile) mobile.innerHTML = errorHtml;
  }

  /**
   * Main render function
   */
  render() {
    this.totalEvents = this.days.reduce((sum, day) => sum + day.events.length, 0);
    this.renderDesktopGrid();
    this.renderMobileAccordion();
  }

  /**
   * Render desktop 7-column grid
   */
  renderDesktopGrid() {
    const container = this.querySelector('[ref="scheduleGrid"]');
    if (!container) return;

    // Find max events per day for row calculation
    const maxEvents = Math.max(...this.days.map(d => d.events.length), 1);
    const numRows = Math.max(maxEvents, 4); // Minimum 4 rows

    // Header row - one column header per day
    let headCells = '';
    this.days.forEach(day => {
      headCells += `
        <th scope="col" class="c-truck-schedule__day-header">
          <span class="c-truck-schedule__day-header-inner">
            <span class="c-truck-schedule__day-name">${day.dayAbbr}&nbsp;</span>
            <span class="c-truck-schedule__day-date">${day.dayDate}</span>
          </span>
        </th>
      `;
    });

    // Schedule rows - skip entirely empty rows
    let bodyRows = '';
    for (let row = 0; row < numRows; row++) {
      const rowHasEvents = this.days.some(day => day.events[row]);
      if (!rowHasEvents) continue;

      let cells = '';

      this.days.forEach(day => {
        const event = day.events[row];

        if (event) {
          const closedClass = event.isAllDay ? ' c-truck-schedule__cell--closed' : '';
          cells += `
            <td class="c-truck-schedule__cell c-truck-schedule__cell--filled${closedClass}">
              <div class="c-truck-schedule__cell-inner">
                <div class="c-truck-schedule__cell-content">
                  <p class="c-truck-schedule__location-name">${event.parsedLocation.name}</p>
                  <div class="c-truck-schedule__time">
                    <svg class="c-truck-schedule__time-icon" width="14" height="14" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                      <path fill-rule="evenodd" clip-rule="evenodd" d="M6 1.45C3.4871 1.45 1.45 3.4871 1.45 6C1.45 8.5129 3.4871 10.55 6 10.55C8.5129 10.55 10.55 8.5129 10.55 6C10.55 3.4871 8.5129 1.45 6 1.45ZM0.55 6C0.55 2.99005 2.99005 0.55 6 0.55C9.00995 0.55 11.45 2.99005 11.45 6C11.45 9.00995 9.00995 11.45 6 11.45C2.99005 11.45 0.55 9.00995 0.55 6Z" fill="currentColor"/><path fill-rule="evenodd" clip-rule="evenodd" d="M6.45 3.05V5.55H8.45V6.45H6C5.75147 6.45 5.55 6.24853 5.55 6V3.05H6.45Z" fill="currentColor"/>
                    </svg>
                    <span class="c-truck-schedule__time-text">${event.formattedTime}</span>
                  </div>
                  ${this.renderAddress(event.parsedLocation)}
                </div>
                ${this.orderUrl && !event.isAllDay ? `
                  <a href="${this.orderUrl}" class="c-truck-schedule__order-btn">
                    ${this.orderButtonText}
                  </a>
                ` : ''}
              </div>
            </td>
          `;
        } else {
          cells += '<td class="c-truck-schedule__cell c-truck-schedule__cell--empty"></td>';
        }
      });

      bodyRows += `<tr class="c-truck-schedule__schedule-row">${cells}</tr>`;
    }

    // Show no-schedule message below the date headers when empty
    if (this.totalEvents === 0) {
      const noScheduleText = this.dataset.noScheduleText || 'No upcoming schedule at this location. Check back soon!';
      bodyRows += `<tr><td colspan="${this.days.length}" class="c-truck-schedule__empty-cell"><div class="c-truck-schedule__empty-state"><p class="c-truck-schedule__empty-state-text">${noScheduleText}</p></div></td></tr>`;
    }

    container.innerHTML = `
      <table class="c-truck-schedule__table">
        <thead>
          <tr class="c-truck-schedule__header-row">${headCells}</tr>
        </thead>
        <tbody>${bodyRows}</tbody>
      </table>
    `;
  }

  /**
   * Render mobile accordion view
   */
  renderMobileAccordion() {
    const container = this.querySelector('[ref="scheduleMobile"]');
    if (!container) return;

    let html = '';

    // On mobile, if no events at all, skip the days list and show only the empty state message
    if (this.totalEvents === 0) {
      const noScheduleText = this.dataset.noScheduleText || 'No upcoming schedule at this location. Check back soon!';
      container.innerHTML = `<div class="c-truck-schedule__empty-state"><p class="c-truck-schedule__empty-state-text">${noScheduleText}</p></div>`;
      return;
    }

    if (this.disableMobileAccordion) {
      html = this.renderMobileFlat();
    } else {
      html = this.renderMobileAccordionMarkup();
    }

    container.innerHTML = html;
  }

  /**
   * Render mobile flat view (no accordion)
   */
  renderMobileFlat() {
    let html = '';

    this.days.forEach((day, index) => {
      const hasEvents = day.events.length > 0;
      const isFirst = index === 0;

      html += `
        <div class="c-truck-schedule__flat-day${isFirst ? ' c-truck-schedule__flat-day--first' : ''}">
          <div class="c-truck-schedule__flat-day-header">
            <p class="c-truck-schedule__accordion-day-inline">
              <span class="c-truck-schedule__accordion-day-name">${day.dayAbbr},&nbsp;</span>
              <span class="c-truck-schedule__accordion-day-date">${day.dayDate}</span>
            </p>
          </div>
          <div class="c-truck-schedule__accordion-content">
      `;

      if (hasEvents) {
        day.events.forEach(event => {
          const closedClass = event.isAllDay ? ' c-truck-schedule__mobile-event--closed' : '';
          html += `
            <div class="c-truck-schedule__mobile-event${closedClass}">
              <hr class="c-truck-schedule__mobile-divider" />
              <div class="c-truck-schedule__mobile-event-body">
                <div class="c-truck-schedule__mobile-event-info">
                  <p class="c-truck-schedule__location-name">${event.parsedLocation.name}</p>
                  <div class="c-truck-schedule__time">
                    <svg class="c-truck-schedule__time-icon" width="14" height="14" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                      <path fill-rule="evenodd" clip-rule="evenodd" d="M6 1.45C3.4871 1.45 1.45 3.4871 1.45 6C1.45 8.5129 3.4871 10.55 6 10.55C8.5129 10.55 10.55 8.5129 10.55 6C10.55 3.4871 8.5129 1.45 6 1.45ZM0.55 6C0.55 2.99005 2.99005 0.55 6 0.55C9.00995 0.55 11.45 2.99005 11.45 6C11.45 9.00995 9.00995 11.45 6 11.45C2.99005 11.45 0.55 9.00995 0.55 6Z" fill="currentColor"/><path fill-rule="evenodd" clip-rule="evenodd" d="M6.45 3.05V5.55H8.45V6.45H6C5.75147 6.45 5.55 6.24853 5.55 6V3.05H6.45Z" fill="currentColor"/>
                    </svg>
                    <span class="c-truck-schedule__time-text">${event.formattedTime}</span>
                  </div>
                  ${this.renderAddress(event.parsedLocation)}
                </div>
                ${this.orderUrl && !event.isAllDay ? `
                  <a href="${this.orderUrl}" class="c-truck-schedule__order-btn">
                    ${this.orderButtonText}
                  </a>
                ` : ''}
              </div>
            </div>
          `;
        });
        html += '<hr class="c-truck-schedule__mobile-divider" />';
      } else {
        html += `<p class="c-truck-schedule__no-schedule">${this.dataset.noStopsText || 'No scheduled stops for this day'}</p>`;
      }

      html += `
          </div>
        </div>
      `;
    });

    return html;
  }

  /**
   * Render mobile accordion markup (default behavior)
   */
  renderMobileAccordionMarkup() {
    let html = '';
    const firstDayWithEvents = this.days.findIndex(day => day.events.length > 0);

    this.days.forEach((day, index) => {
      const hasEvents = day.events.length > 0;
      const isFirst = index === 0;
      const isNextWithEvents = index === firstDayWithEvents;
      const openAttrs = isNextWithEvents
        ? ' open-by-default-on-mobile open-by-default-on-desktop'
        : hasEvents
          ? ' open-by-default-on-mobile open-by-default-on-desktop'
          : '';

      html += `
        <accordion-custom${openAttrs}>
          <details class="c-truck-schedule__accordion-details${!hasEvents ? ' c-truck-schedule__accordion-details--empty' : ''}${isFirst ? ' c-truck-schedule__accordion-details--first' : ''}">
            <summary class="c-truck-schedule__accordion-summary">
              <p class="c-truck-schedule__accordion-day-inline">
                <span class="c-truck-schedule__accordion-day-name">${day.dayAbbr},&nbsp;</span>
                <span class="c-truck-schedule__accordion-day-date">${day.dayDate}</span>
              </p>
              <span class="c-truck-schedule__accordion-icon">
                <svg class="c-truck-schedule__icon-plus" width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                  <path d="M19 12.998H13V18.998H11V12.998H5V10.998H11V4.998H13V10.998H19V12.998Z" fill="currentColor"/>
                </svg>
                <svg class="c-truck-schedule__icon-minus" width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                  <path d="M19 12.998H5V10.998H19V12.998Z" fill="currentColor"/>
                </svg>
              </span>
            </summary>
            <div class="c-truck-schedule__accordion-content">
      `;

      if (hasEvents) {
        day.events.forEach((event, eventIndex) => {
          const closedClass = event.isAllDay ? ' c-truck-schedule__mobile-event--closed' : '';
          html += `
            <div class="c-truck-schedule__mobile-event${closedClass}">
              <hr class="c-truck-schedule__mobile-divider" />
              <div class="c-truck-schedule__mobile-event-body">
                <div class="c-truck-schedule__mobile-event-info">
                  <p class="c-truck-schedule__location-name">${event.parsedLocation.name}</p>
                  <div class="c-truck-schedule__time">
                    <svg class="c-truck-schedule__time-icon" width="14" height="14" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                      <path fill-rule="evenodd" clip-rule="evenodd" d="M6 1.45C3.4871 1.45 1.45 3.4871 1.45 6C1.45 8.5129 3.4871 10.55 6 10.55C8.5129 10.55 10.55 8.5129 10.55 6C10.55 3.4871 8.5129 1.45 6 1.45ZM0.55 6C0.55 2.99005 2.99005 0.55 6 0.55C9.00995 0.55 11.45 2.99005 11.45 6C11.45 9.00995 9.00995 11.45 6 11.45C2.99005 11.45 0.55 9.00995 0.55 6Z" fill="currentColor"/><path fill-rule="evenodd" clip-rule="evenodd" d="M6.45 3.05V5.55H8.45V6.45H6C5.75147 6.45 5.55 6.24853 5.55 6V3.05H6.45Z" fill="currentColor"/>
                    </svg>
                    <span class="c-truck-schedule__time-text">${event.formattedTime}</span>
                  </div>
                  ${this.renderAddress(event.parsedLocation)}
                </div>
                ${this.orderUrl && !event.isAllDay ? `
                  <a href="${this.orderUrl}" class="c-truck-schedule__order-btn">
                    ${this.orderButtonText}
                  </a>
                ` : ''}
              </div>
            </div>
          `;
        });
        // Closing divider after last event
        html += '<hr class="c-truck-schedule__mobile-divider" />';
      } else {
        html += `<p class="c-truck-schedule__no-schedule">${this.dataset.noStopsText || 'No scheduled stops for this day'}</p>`;
      }

      html += `
            </div>
          </details>
        </accordion-custom>
      `;
    });

    return html;
  }

  /* ==========================================================================
     Cross-Brand Secondary Calendar
     ========================================================================== */

  /**
   * Initialize the cross-brand banner and pre-fetch secondary events.
   * The banner stays hidden until we confirm the other brand has events in
   * this location's state (see fetchSecondaryEvents -> revealBannerIfNearby).
   */
  initCrossBrand() {
    const container = this.querySelector('[ref="crossBrandContainer"]');
    const toggle = this.querySelector('[ref="crossBrandToggle"]');

    if (!container || !toggle) return;

    this.crossBrandExpanded = false;
    this.expandLabel = container.dataset.expandLabel || 'View the Truck Schedule';
    this.collapseLabel = container.dataset.collapseLabel || 'Hide the Schedule';

    // The schedule animates open/closed via the `.is-expanded` class, so it must
    // stay in the layout (a `hidden`/display:none element can't transition).
    // Drop the boolean `hidden` and mark it collapsed for assistive tech instead.
    const calendar = this.querySelector('[ref="secondaryCalendar"]');
    if (calendar) {
      calendar.removeAttribute('hidden');
      calendar.setAttribute('aria-hidden', 'true');
      calendar.inert = true;
    }

    // Pre-fetch secondary events, then reveal the banner only if "serving nearby".
    this.fetchSecondaryEvents().then(() => this.revealBannerIfNearby());

    // Toggle the schedule open/closed on click
    toggle.addEventListener('click', () => {
      this.handleCrossBrandToggle(!this.crossBrandExpanded);
    });
  }

  /**
   * Fetch events from the secondary calendar API
   */
  async fetchSecondaryEvents() {
    try {
      const response = await fetch(this.secondaryApiUrl);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      if (data.error) {
        throw new Error(data.error);
      }

      // Keep the other brand's events unfiltered so the schedule mirrors exactly
      // what that brand shows on its own location page (a truck routinely
      // crosses state lines, e.g. the Kentucky truck stopping in Indiana).
      this.secondaryEvents = data.events || [];

      this.initSecondaryDays();
      this.assignSecondaryEventsToDay();
      this.secondaryReady = true;
    } catch (error) {
      console.error('Truck Schedule: Failed to fetch secondary events', error);
      this.secondaryReady = false;
    }
  }

  /**
   * Reveal the cross-brand banner only when the other brand has at least one
   * upcoming stop in this location's state within the 7-day window. The state
   * only gates the banner; the schedule itself is rendered unfiltered.
   */
  revealBannerIfNearby() {
    const container = this.querySelector('[ref="crossBrandContainer"]');
    if (!container) return;

    const stateRegex = this.crossBrandState
      ? new RegExp(`\\b${this.crossBrandState}\\b`, 'i')
      : null;
    const isNearby = this.secondaryReady && this.secondaryDays.some(day =>
      day.events.some(event =>
        !stateRegex || stateRegex.test(event.location || '') || stateRegex.test(event.title || '')
      )
    );

    if (isNearby) {
      container.removeAttribute('hidden');
    } else {
      container.setAttribute('hidden', '');
    }
  }

  /**
   * Handle the cross-brand banner expand/collapse
   */
  handleCrossBrandToggle(shouldExpand) {
    const calendar = this.querySelector('[ref="secondaryCalendar"]');
    const toggle = this.querySelector('[ref="crossBrandToggle"]');
    const toggleLabel = this.querySelector('[ref="toggleLabel"]');
    if (!calendar || !toggle) return;

    this.crossBrandExpanded = shouldExpand;
    toggle.setAttribute('aria-expanded', String(shouldExpand));
    if (toggleLabel) {
      toggleLabel.textContent = shouldExpand ? this.collapseLabel : this.expandLabel;
    }

    if (shouldExpand) {
      // Populate before expanding so the height animation targets real content.
      if (this.secondaryReady) {
        this.renderSecondaryCalendar();
      } else {
        this.renderSecondaryLoading();
      }
      calendar.removeAttribute('inert');
      calendar.setAttribute('aria-hidden', 'false');
      // Add the class on the next frame so the browser registers the collapsed
      // start state first and actually animates to open.
      requestAnimationFrame(() => calendar.classList.add('is-expanded'));
    } else {
      calendar.classList.remove('is-expanded');
      calendar.setAttribute('aria-hidden', 'true');
      calendar.inert = true;
    }
  }

  /**
   * Render loading state in secondary calendar
   */
  renderSecondaryLoading() {
    const grid = this.querySelector('[ref="secondaryGrid"]');
    const mobile = this.querySelector('[ref="secondaryMobile"]');

    const loadingHtml = '<div class="c-truck-schedule__loading">Loading schedule...</div>';
    if (grid) grid.innerHTML = loadingHtml;
    if (mobile) mobile.innerHTML = loadingHtml;
  }

  /**
   * Render the secondary calendar (grid + mobile)
   */
  renderSecondaryCalendar() {
    this.renderSecondaryDesktopGrid();
    this.renderSecondaryMobileAccordion();
  }

  /**
   * Render secondary desktop grid
   */
  renderSecondaryDesktopGrid() {
    const container = this.querySelector('[ref="secondaryGrid"]');
    if (!container) return;

    const orderUrl = this.hideSecondaryOrder ? '' : this.secondaryOrderUrl;
    const orderButtonText = this.secondaryOrderButtonText;
    const days = this.secondaryDays;
    const totalEvents = days.reduce((sum, day) => sum + day.events.length, 0);

    const maxEvents = Math.max(...days.map(d => d.events.length), 1);
    const numRows = Math.max(maxEvents, 4);

    // Header row - one column header per day
    let headCells = '';
    for (const day of days) {
      headCells += `
        <th scope="col" class="c-truck-schedule__day-header">
          <span class="c-truck-schedule__day-header-inner">
            <span class="c-truck-schedule__day-name">${day.dayAbbr}&nbsp;</span>
            <span class="c-truck-schedule__day-date">${day.dayDate}</span>
          </span>
        </th>
      `;
    }

    let bodyRows = '';
    for (let row = 0; row < numRows; row++) {
      const rowHasEvents = days.some(day => day.events[row]);
      if (!rowHasEvents) continue;

      let cells = '';

      for (const day of days) {
        const event = day.events[row];

        if (event) {
          const closedClass = event.isAllDay ? ' c-truck-schedule__cell--closed' : '';
          cells += `
            <td class="c-truck-schedule__cell c-truck-schedule__cell--filled${closedClass}">
              <div class="c-truck-schedule__cell-inner">
                <div class="c-truck-schedule__cell-content">
                  <p class="c-truck-schedule__location-name">${event.parsedLocation.name}</p>
                  <div class="c-truck-schedule__time">
                    <svg class="c-truck-schedule__time-icon" width="14" height="14" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                      <path fill-rule="evenodd" clip-rule="evenodd" d="M6 1.45C3.4871 1.45 1.45 3.4871 1.45 6C1.45 8.5129 3.4871 10.55 6 10.55C8.5129 10.55 10.55 8.5129 10.55 6C10.55 3.4871 8.5129 1.45 6 1.45ZM0.55 6C0.55 2.99005 2.99005 0.55 6 0.55C9.00995 0.55 11.45 2.99005 11.45 6C11.45 9.00995 9.00995 11.45 6 11.45C2.99005 11.45 0.55 9.00995 0.55 6Z" fill="currentColor"/><path fill-rule="evenodd" clip-rule="evenodd" d="M6.45 3.05V5.55H8.45V6.45H6C5.75147 6.45 5.55 6.24853 5.55 6V3.05H6.45Z" fill="currentColor"/>
                    </svg>
                    <span class="c-truck-schedule__time-text">${event.formattedTime}</span>
                  </div>
                  ${this.renderAddress(event.parsedLocation)}
                </div>
                ${orderUrl && !event.isAllDay ? `
                  <a href="${orderUrl}" class="c-truck-schedule__order-btn">
                    ${orderButtonText}
                  </a>
                ` : ''}
              </div>
            </td>
          `;
        } else {
          cells += '<td class="c-truck-schedule__cell c-truck-schedule__cell--empty"></td>';
        }
      }

      bodyRows += `<tr class="c-truck-schedule__schedule-row">${cells}</tr>`;
    }

    if (totalEvents === 0) {
      const noScheduleText = this.dataset.noScheduleText || 'No upcoming schedule at this location. Check back soon!';
      bodyRows += `<tr><td colspan="${days.length}" class="c-truck-schedule__empty-cell"><div class="c-truck-schedule__empty-state"><p class="c-truck-schedule__empty-state-text">${noScheduleText}</p></div></td></tr>`;
    }

    container.innerHTML = `
      <table class="c-truck-schedule__table">
        <thead>
          <tr class="c-truck-schedule__header-row">${headCells}</tr>
        </thead>
        <tbody>${bodyRows}</tbody>
      </table>
    `;
  }

  /**
   * Render an address anchor/span for the cross-brand mobile accordion.
   * Uses the dedicated c-cb-schedule namespace (kept separate from the shared
   * c-truck-schedule styles).
   */
  renderCbAddress(parsedLocation) {
    if (!parsedLocation.address) return '';
    if (parsedLocation.mapUrl) {
      return `<a href="${parsedLocation.mapUrl}" class="c-cb-schedule__address" target="_blank" rel="noopener noreferrer">${parsedLocation.address}</a>`;
    }
    return `<span class="c-cb-schedule__address">${parsedLocation.address}</span>`;
  }

  /**
   * Render the cross-brand mobile schedule (Figma 754:389).
   * A self-contained native <details> accordion in its own c-cb-schedule
   * namespace so it never inherits or collides with the shared
   * c-truck-schedule styles. The first day with events opens by default.
   */
  renderSecondaryMobileAccordion() {
    const container = this.querySelector('[ref="secondaryMobile"]');
    if (!container) return;

    const orderUrl = this.hideSecondaryOrder ? '' : this.secondaryOrderUrl;
    const orderButtonText = this.secondaryOrderButtonText;
    const days = this.secondaryDays;
    const totalEvents = days.reduce((sum, day) => sum + day.events.length, 0);

    if (totalEvents === 0) {
      const noScheduleText = this.dataset.noScheduleText || 'No upcoming schedule at this location. Check back soon!';
      container.innerHTML = `<p class="c-cb-schedule__empty">${noScheduleText}</p>`;
      return;
    }

    const clockIcon = `
      <svg class="c-cb-schedule__rowicon" width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
        <path fill-rule="evenodd" clip-rule="evenodd" d="M6 1.45C3.4871 1.45 1.45 3.4871 1.45 6C1.45 8.5129 3.4871 10.55 6 10.55C8.5129 10.55 10.55 8.5129 10.55 6C10.55 3.4871 8.5129 1.45 6 1.45ZM0.55 6C0.55 2.99005 2.99005 0.55 6 0.55C9.00995 0.55 11.45 2.99005 11.45 6C11.45 9.00995 9.00995 11.45 6 11.45C2.99005 11.45 0.55 9.00995 0.55 6Z" fill="currentColor"/><path fill-rule="evenodd" clip-rule="evenodd" d="M6.45 3.05V5.55H8.45V6.45H6C5.75147 6.45 5.55 6.24853 5.55 6V3.05H6.45Z" fill="currentColor"/>
      </svg>`;

    const firstDayWithEvents = days.findIndex(day => day.events.length > 0);

    let html = '';
    days.forEach((day, index) => {
      const hasEvents = day.events.length > 0;
      const isOpen = index === firstDayWithEvents;

      let panel = '';
      if (hasEvents) {
        day.events.forEach(event => {
          panel += `
            <div class="c-cb-schedule__event">
              <p class="c-cb-schedule__location">${event.parsedLocation.name}</p>
              <div class="c-cb-schedule__meta">
                <div class="c-cb-schedule__row">
                  ${clockIcon}
                  <span class="c-cb-schedule__rowtext">${event.formattedTime}</span>
                </div>
              </div>
              ${this.renderCbAddress(event.parsedLocation)}
              ${orderUrl && !event.isAllDay ? `<a href="${orderUrl}" class="c-cb-schedule__order">${orderButtonText}</a>` : ''}
            </div>
          `;
        });
      } else {
        panel += `<p class="c-cb-schedule__none">${this.dataset.noStopsText || 'No scheduled stops for this day'}</p>`;
      }

      html += `
        <details class="c-cb-schedule__day"${isOpen ? ' open' : ''}>
          <summary class="c-cb-schedule__summary">
            <span class="c-cb-schedule__dayhead">
              <span class="c-cb-schedule__dayname">${day.dayAbbr}</span>
              <span class="c-cb-schedule__daydate">${day.dayDate}</span>
            </span>
            <span class="c-cb-schedule__toggle" aria-hidden="true">
              <svg class="c-cb-schedule__icon c-cb-schedule__icon--plus" width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M10 2.93481V9.93481M10 9.93481V16.9348M10 9.93481H3M10 9.93481H17" stroke="currentColor" stroke-width="1.25"/></svg>
              <svg class="c-cb-schedule__icon c-cb-schedule__icon--minus" width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M3 9.93481H10H17" stroke="currentColor" stroke-width="1.25"/></svg>
            </span>
          </summary>
          <div class="c-cb-schedule__panel">${panel}</div>
        </details>
      `;
    });

    container.innerHTML = html;
  }
}

// Register the custom element
if (!customElements.get('truck-schedule-component')) {
  customElements.define('truck-schedule-component', TruckScheduleComponent);
}
